import {
    StreamsByStreamKeyState,
    StreamState,
} from 'streamsState/store/streamedData/StreamedDataState';
import State from 'store/State';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useSelector } from 'react-redux';
import { WidgetConfigWithSymbol } from 'widgets/WidgetSettingsEditing/WidgetSettingsSymbolEditor';
import { ChannelDestination } from 'communication/Channel';
import { StreamElementDataMessage } from 'communication/types/StreamElement';
import {
    createByStreamKeyReducer,
    createProcessStreamByStreamKeyDataMessage,
} from 'streamsState/store/streamedData/createByStreamKeyReducer';
import { WidgetId } from 'widgets/WidgetId';
import { usePortfolioActiveInputUsesOrdersStream } from 'portfolio/store/hooks';
import { DataServices } from 'store/Services';
import { useServices } from 'app/hooks';
import { useEffect, useState } from 'react';

export function buildStreamedDataStreamSliceSelectors<
    TStreamDataState,
    TConfigState extends WidgetConfigWithSymbol,
>(
    selectStreamsState: (
        state: State
    ) => StreamsByStreamKeyState<TStreamDataState>,
    createWidgetRequestUid: (
        state: State,
        services: DataServices,
        id: WidgetId,
        activeInputUsesOrdersStream: boolean
    ) => Promise<string | undefined>,
    toRequestUidForConfigOrSymbol: (
        symbol: string,
        config: TConfigState,
        qty?: number
    ) => string
) {
    const selectStreamedDataState = (state: State, streamKey: string) =>
        selectStreamsState(state)[streamKey]?.data;

    const selectMessagesState = (state: State, streamKey: string) =>
        selectStreamsState(state)[streamKey]?.messages ?? [];

    function useWidgetStreamKey() {
        const { widgetType, widgetKey } = useWidgetContext();
        const [streamKey, setStreamkey] = useState<string | undefined>('');
        const state = useSelector((s: State) => s);
        const activeInputUsesOrdersStream =
            usePortfolioActiveInputUsesOrdersStream();
        const services = useServices();
        useEffect(() => {
            createWidgetRequestUid(
                state,
                services,
                {
                    widgetType,
                    widgetKey,
                },
                activeInputUsesOrdersStream
            ).then((res) => {
                setStreamkey(res);
            });
        }, [
            state,
            services,
            widgetKey,
            widgetType,
            activeInputUsesOrdersStream,
        ]);

        return streamKey;
    }
    function useWidgetStreamData() {
        const streamKey = useWidgetStreamKey();

        return useSelector((state: State) => {
            return streamKey
                ? selectStreamedDataState(state, streamKey)
                : undefined;
        });
    }

    function useWidgetMessages() {
        const streamKey = useWidgetStreamKey();

        return useSelector((state: State) => {
            return streamKey ? selectMessagesState(state, streamKey) : [];
        });
    }

    function useSymbolStreamedData(
        symbol: string,
        config: TConfigState,
        qty?: number
    ) {
        const streamKey = toRequestUidForConfigOrSymbol(symbol, config, qty);
        return useSelector((state: State) =>
            selectStreamedDataState(state, streamKey)
        );
    }

    function useStreamedDataMessages(
        symbol: string,
        config: TConfigState,
        qty?: number
    ) {
        return useSelector((state: State) => {
            const streamKey = toRequestUidForConfigOrSymbol(
                symbol,
                config,
                qty
            );
            return streamKey ? selectMessagesState(state, streamKey) : [];
        });
    }

    return {
        selectStreamedDataState,
        useWidgetStreamData,
        useWidgetMessages,
        useSymbolStreamedData,
        useStreamedDataMessages,
    };
}

export function buildStreamedDataStreamSlice<
    TStreamDataState,
    TConfigState extends WidgetConfigWithSymbol,
    TDataElement,
    TStreamState extends StreamState<TStreamDataState>,
>(
    channel: ChannelDestination,
    initialStreamState: TStreamState,

    selectStreamsState: (
        state: State
    ) => StreamsByStreamKeyState<TStreamDataState>,
    createWidgetRequestUid: (
        state: State,
        services: DataServices,
        id: WidgetId,
        activeInputUsesOrdersStream: boolean
    ) => Promise<string | undefined>,
    toRequestUidForConfigOrSymbol: (
        symbol: string,
        config: TConfigState
    ) => string,
    processDataStreamElement: (
        streamDataElement: TDataElement,
        nextState: TStreamState
    ) => void,
    additionalReducer: (
        streamDataElement: StreamElementDataMessage<TDataElement>,
        nextState: TStreamState
    ) => void = () => {
        return;
    }
) {
    const selectors = buildStreamedDataStreamSliceSelectors(
        selectStreamsState,
        createWidgetRequestUid,
        toRequestUidForConfigOrSymbol
    );

    const processDataStreamMessage = createProcessStreamByStreamKeyDataMessage<
        TStreamDataState,
        TDataElement,
        TStreamState
    >(processDataStreamElement, additionalReducer);

    const reducer = createByStreamKeyReducer<
        TStreamDataState,
        TDataElement,
        TStreamState
    >(channel, initialStreamState, processDataStreamMessage);

    return { selectors, reducer };
}
